import React from 'react';
import { Box, Skeleton, Paper, alpha } from '@mui/material';

const RiskManagementSkeleton = () => {
  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh',
    }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Skeleton 
              variant="rounded" 
              width={64} 
              height={64}
              animation="wave"
              sx={{ 
                borderRadius: 2.5,
                bgcolor: alpha('#6366f1', 0.1)
              }}
            />
            <Box>
              <Skeleton 
                variant="text" 
                width={220} 
                height={40}
                animation="wave"
                sx={{ mb: 0.5 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Skeleton 
                  variant="text" 
                  width={120} 
                  height={20}
                  animation="wave"
                />
                <Skeleton 
                  variant="text" 
                  width={80} 
                  height={20}
                  animation="wave"
                />
              </Box>
            </Box>
          </Box>
          
          {/* Refresh Button */}
          <Skeleton 
            variant="rounded" 
            width={110} 
            height={32}
            animation="wave"
            sx={{ borderRadius: 0.75 }}
          />
        </Box>
      </Box>

      {/* Market Info Banner */}
      <Paper
        elevation={0}
        sx={{ 
          mb: 2.5,
          px: 2,
          py: 1.5,
          borderRadius: 1.5,
          bgcolor: alpha('#10b981', 0.06),
          border: '1px solid',
          borderColor: alpha('#10b981', 0.15),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Skeleton 
            variant="rounded" 
            width={32} 
            height={32}
            animation="wave"
            sx={{ 
              borderRadius: 1,
              bgcolor: alpha('#10b981', 0.15)
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Skeleton 
              variant="text" 
              width="60%" 
              height={20}
              animation="wave"
            />
          </Box>
        </Box>
      </Paper>

      {/* CSS Grid Layout */}
      <Box sx={{
        display: 'grid',
        gap: { xs: 2, sm: 2.5 },
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)',
        },
        gridAutoRows: 'auto',
      }}>
        {/* Row 1: Critical Metrics - 3 Gauges */}
        {[1, 2, 3].map((i) => (
          <Paper
            key={`gauge-${i}`}
            elevation={0}
            sx={{
              gridColumn: i === 3 ? { xs: '1', sm: '1 / 3', lg: '3' } : 'auto',
              p: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              background: 'white',
            }}
          >
            {/* Title */}
            <Skeleton 
              variant="text" 
              width="60%" 
              height={24}
              animation="wave"
              sx={{ mb: 2 }}
            />
            
            {/* Gauge Circle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Skeleton 
                variant="circular" 
                width={140} 
                height={140}
                animation="wave"
                sx={{ bgcolor: alpha('#6366f1', 0.08) }}
              />
            </Box>
            
            {/* Subtitle */}
            <Skeleton 
              variant="text" 
              width="80%" 
              height={18}
              animation="wave"
              sx={{ mx: 'auto' }}
            />
          </Paper>
        ))}

        {/* Row 2: Portfolio Health & Trading Gates */}
        <Paper
          elevation={0}
          sx={{
            gridColumn: { xs: '1', sm: '1 / 3', lg: '1 / 3' },
            p: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            background: 'white',
          }}
        >
          <Skeleton 
            variant="text" 
            width={180} 
            height={28}
            animation="wave"
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            {[1, 2, 3, 4].map((i) => (
              <Box key={i} sx={{ flex: 1 }}>
                <Skeleton 
                  variant="text" 
                  width="60%" 
                  height={16}
                  animation="wave"
                  sx={{ mb: 0.5 }}
                />
                <Skeleton 
                  variant="text" 
                  width="80%" 
                  height={32}
                  animation="wave"
                />
              </Box>
            ))}
          </Box>
          <Skeleton 
            variant="rectangular" 
            width="100%" 
            height={200}
            animation="wave"
            sx={{ borderRadius: 1.5, bgcolor: alpha('#6366f1', 0.05) }}
          />
        </Paper>

        <Paper
          elevation={0}
          sx={{
            gridColumn: { xs: '1', sm: '1 / 3', lg: '3' },
            p: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            background: 'white',
          }}
        >
          <Skeleton 
            variant="text" 
            width={140} 
            height={28}
            animation="wave"
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {[1, 2, 3, 4].map((i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: alpha('#6366f1', 0.03),
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Skeleton 
                  variant="circular" 
                  width={32} 
                  height={32}
                  animation="wave"
                />
                <Box sx={{ flex: 1 }}>
                  <Skeleton 
                    variant="text" 
                    width="70%" 
                    height={18}
                    animation="wave"
                    sx={{ mb: 0.5 }}
                  />
                  <Skeleton 
                    variant="text" 
                    width="50%" 
                    height={14}
                    animation="wave"
                  />
                </Box>
                <Skeleton 
                  variant="rounded" 
                  width={60} 
                  height={24}
                  animation="wave"
                  sx={{ borderRadius: 3 }}
                />
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Row 3: Risk Metrics Grid - Full Width */}
        <Paper
          elevation={0}
          sx={{
            gridColumn: { xs: '1', sm: '1 / 3', lg: '1 / 4' },
            p: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            background: 'white',
          }}
        >
          <Skeleton 
            variant="text" 
            width={160} 
            height={28}
            animation="wave"
            sx={{ mb: 2 }}
          />
          <Box sx={{ 
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            }
          }}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Box
                key={i}
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: alpha('#6366f1', 0.03),
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Skeleton 
                  variant="text" 
                  width="80%" 
                  height={16}
                  animation="wave"
                  sx={{ mb: 1 }}
                />
                <Skeleton 
                  variant="text" 
                  width="60%" 
                  height={28}
                  animation="wave"
                />
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Row 4: Risk Matrix & Exposure Timeline */}
        <Paper
          elevation={0}
          sx={{
            gridColumn: { xs: '1', sm: '1 / 3', lg: '1 / 2' },
            p: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            background: 'white',
          }}
        >
          <Skeleton 
            variant="text" 
            width={140} 
            height={28}
            animation="wave"
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Skeleton 
              variant="rectangular" 
              width={280} 
              height={280}
              animation="wave"
              sx={{ borderRadius: 2, bgcolor: alpha('#6366f1', 0.05) }}
            />
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            gridColumn: { xs: '1', sm: '1 / 3', lg: '2 / 4' },
            p: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            background: 'white',
          }}
        >
          <Skeleton 
            variant="text" 
            width={180} 
            height={28}
            animation="wave"
            sx={{ mb: 2 }}
          />
          <Skeleton 
            variant="rectangular" 
            width="100%" 
            height={280}
            animation="wave"
            sx={{ borderRadius: 1.5, bgcolor: alpha('#6366f1', 0.05) }}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default RiskManagementSkeleton;

